import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 15;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          // refresh user to get new plan
          try {
            const me = await api.get("/auth/me");
            setUser(me.data);
          } catch {}
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch {}
      attempts += 1;
      setAttempt(attempts);
      if (attempts >= MAX_POLLS) {
        setStatus("timeout");
      } else {
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, setUser]);

  return (
    <div className="max-w-xl mx-auto px-6 py-32 text-center">
      {status === "checking" && (
        <>
          <Loader2 className="w-12 h-12 text-[#CCFF00] mx-auto animate-spin mb-6" />
          <h1 className="font-display font-black text-4xl tracking-tighter mb-3">Confirming payment…</h1>
          <p className="text-neutral-400 font-mono text-sm">
            check {attempt}/{MAX_POLLS}
          </p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 className="w-12 h-12 text-[#CCFF00] mx-auto mb-6" />
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">// welcome to</p>
          <h1 className="font-display font-black text-5xl tracking-tighter mb-4">
            DAP {user?.plan === "business" ? "Business" : "Pro"}.
          </h1>
          <p className="text-neutral-400 mb-8">Deal Copilot and unlimited scans are unlocked.</p>
          <Button
            onClick={() => nav("/app/copilot")}
            className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] h-12 px-6 font-semibold"
            data-testid="payment-success-continue"
          >
            Open Deal Copilot →
          </Button>
        </>
      )}
      {(status === "failed" || status === "error") && (
        <>
          <h1 className="font-display font-black text-5xl tracking-tighter mb-4">Payment didn't go through.</h1>
          <p className="text-neutral-400 mb-8">No worries — nothing was charged.</p>
          <Link to="/billing">
            <Button className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] h-12 px-6 font-semibold">
              Try again
            </Button>
          </Link>
        </>
      )}
      {status === "timeout" && (
        <>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4">Still processing…</h1>
          <p className="text-neutral-400 mb-8">
            Your bank is taking a moment. Refresh this page in a few seconds, or check your dashboard.
          </p>
          <Link to="/app/dashboard">
            <Button className="rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] h-12 px-6 font-semibold">
              Go to dashboard
            </Button>
          </Link>
        </>
      )}
    </div>
  );
}
